// ═══════════════════════════════════════════════════════════════════
//  TOQUM · INSPECTION FORMAT FORMS (J-01 … J-05)
//  Each form renders the real workbook layout, saves to the DB and
//  prints an A4 report matching the original format.
// ═══════════════════════════════════════════════════════════════════
import React, { useState } from 'react';
import {
  T, uid, today, num, Section, Field, Grid, Btn, DynamicTable, Collapse,
  TriState, Tag, Badge, printHTML, DataTable,
} from './Shared';
import {
  FORMAT_REGISTRY, PPM_PARTICIPANTS, PPM_PLAN_ROWS, PPM_PILOT_ROWS, PPM_FABRIC_ROWS,
  PPM_FABRIC_COLS, PPM_FAB_INSPECTION, PPM_SHRINKAGE, PPM_THREAD_ROWS, PPM_THREAD_COLS,
  PPM_SAMPLE_APPROVALS, PPM_LAYERING, PPM_FUSING_COLS, PPM_MACHINE_TYPES, PPM_CT_COLS,
  PPM_PACK_ACCESSORIES, PPM_HOME_TESTS, MCP_COLS, MCP_STEPS, MCP_WASH_STEPS,
  RISK_COLS, INSP_CHECK_SECTIONS, INSP_STYLING_CHECKS, MOISTURE_LIMITS,
  AQL_LEVELS, aqlPlan, FMEA_COLS, FMEA_GUIDE, rpn, rpnColor,
  STD_MEASUREMENT_POINTS,
} from './FormatSchemas';

const fmt = code => FORMAT_REGISTRY.find(f => f.code === code);

// Shared header block for every format
const FormatHeader = ({ code, po, style, extra }) => {
  const f = fmt(code);
  return (
    <div style={{ background:'#1f1f1f', color:'white', padding:'12px 18px', borderRadius:'8px 8px 0 0',
      display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:10 }}>
      <div>
        <div style={{ fontWeight:900, fontSize:14 }}>{f.subject}</div>
        <div style={{ fontSize:10.5, color:'#9aa4b2', marginTop:2 }}>
          ISO Format {f.iso} · Version {f.version} · Section J
        </div>
      </div>
      <div style={{ fontSize:11, textAlign:'right', color:'#cbd5e1' }}>
        {po && <>PO <b style={{ color:'white' }}>{po.poNo}</b> · Style <b style={{ color:'white' }}>{po.styleNo}</b><br/></>}
        {po && <>Buyer {po.brand} · Supplier {po.supplier} · Order Qty {num(po.qty).toLocaleString()}</>}
        {extra}
      </div>
    </div>
  );
};

const SubHead = ({ n, title }) => (
  <div style={{ fontWeight:800, fontSize:11.5, color:T.primary, margin:'12px 0 6px',
    borderLeft:`3px solid ${T.primary}`, paddingLeft:8 }}>{n} {title}</div>
);

// Simple label + TriState row list
const CheckList = ({ items, values, onChange, cols = 2, options }) => (
  <div style={{ display:'grid', gridTemplateColumns:`repeat(${cols},1fr)`, gap:'3px 16px' }}>
    {items.map(it => (
      <div key={it} style={{ display:'flex', alignItems:'center', gap:8, fontSize:11.5, padding:'3px 0',
        borderBottom:'1px solid #f8fafc' }}>
        <span style={{ flex:1 }}>{it}</span>
        <TriState value={values[it]||''} onChange={v=>onChange(it,v)} options={options} />
      </div>
    ))}
  </div>
);

// ═══════════════════════════════════════════════════════════════════
//  J-01 · PRE-PRODUCTION MEETING
// ═══════════════════════════════════════════════════════════════════
export function PPMForm({ po, currentUser, existing, onSave, onCancel }) {
  const [d, setD] = useState(existing || {
    meta:{ ppmDate:today(), unit:'', line:'', merchandiser:'', exFactory:po?.exFactory||'',
           description:po?.styleNo||'', fabrication:'', orderQty:po?.qty||'' },
    participants:{}, plan:{}, pilot:{},
    fabrics: PPM_FABRIC_ROWS.map(r=>({ description:r })),
    fabInspection:{}, shrinkage:{}, threads: PPM_THREAD_ROWS.map(r=>({ process:r })),
    trimsInHouse:'', trimCardApproval:'', trimIssues:[],
    sampleApprovals:{}, sampleComments:'', patternsReleased:'', patternDate:'', patternAction:'', patternResponsible:'',
    cutting:{ pcd:'', consumption:'', ydsDoz:'', specialMarker:'', startDate:'', bookingQty:'', inputDate:'', actualQty:'' },
    layering:{}, fusing:[], numbering:{ partsNotNumbered:'', positions:'', reverseSticker:'', miniMarker:'' },
    bundling:{ standard:'', required:'', special:'' },
    lineDifficulties:'', machines:{}, manpower:{ operatorsPerLine:'', totalPerLine:'', targetPerLine:'', targetPerDay:'' },
    irreversible:[], washing:{ wash:'', shadeBand:'', reason:'', actionDate:'', responsible:'', sewingInstruction:'' },
    mechanical:{ workaid:'', folder:'', shortages:'', responsible:'' },
    qualityPoints:[], optimumTarget:'', ctq:[], cts:[], ctp:[],
    pressing:{ method:'', comments:'' }, folding:{ method:'', comments:'' },
    packing:{ method:'', comments:'' }, packAccessories:{},
    history:{ buyer:po?.brand||'', style:po?.styleNo||'', unit:'', fabrication:'', thread:'', washing:'', dhu:'', eff:'', topDefects:'' },
    homeTests:{}, generalComments:'',
  });
  const set = (path, val) => setD(p => ({ ...p, [path]: val }));
  const setIn = (grp, k, v) => setD(p => ({ ...p, [grp]:{ ...p[grp], [k]:v } }));

  const save = () => onSave({
    id: existing?.id || uid(), format:'PPM', iso: fmt('PPM').iso, poNo: po?.poNo, styleNo: po?.styleNo,
    supplier: po?.supplier, brand: po?.brand, date: d.meta.ppmDate, preparedBy: currentUser.name,
    status:'Completed', data: d,
  });

  const print = () => printHTML(`PPM ${po?.poNo||''}`, `
    <h1>TOQUM · Pre-Production Meeting Minutes</h1>
    <div>Format ${fmt('PPM').iso} v${fmt('PPM').version} · Buyer: ${po?.brand||''} · Style: ${po?.styleNo||''}</div>
    <table><tr><td><b>Order No:</b> ${po?.poNo||''}</td><td><b>PPM Date:</b> ${d.meta.ppmDate}</td>
      <td><b>Order Qty:</b> ${d.meta.orderQty}</td><td><b>Unit:</b> ${d.meta.unit}</td></tr>
      <tr><td><b>Description:</b> ${d.meta.description}</td><td><b>Fabrication:</b> ${d.meta.fabrication}</td>
      <td><b>Merchandiser:</b> ${d.meta.merchandiser}</td><td><b>Line:</b> ${d.meta.line}</td></tr>
      <tr><td colspan="4"><b>Ex-Factory Date:</b> ${d.meta.exFactory}</td></tr></table>
    <h3>1. Participants</h3><table><tr><th>Designation</th><th>Name</th><th>Designation</th><th>Name</th></tr>
      ${PPM_PARTICIPANTS.reduce((acc,p,i)=>{ if(i%2===0) acc.push([p]); else acc[acc.length-1].push(p); return acc; },[])
        .map(pair=>`<tr>${pair.map(p=>`<td>${p}</td><td>${d.participants[p]||''}</td>`).join('')}</tr>`).join('')}</table>
    <h3>1.1 Production Plan</h3><table><tr><th>Section</th><th>Start</th><th>End</th><th>Stage</th><th>Date</th></tr>
      ${PPM_PLAN_ROWS.map((r,i)=>`<tr><td>${r.label}</td><td>${d.plan[r.key+'_start']||''}</td><td>${d.plan[r.key+'_end']||''}</td>
        <td>${PPM_PILOT_ROWS[i]?.label||''}</td><td>${PPM_PILOT_ROWS[i]?d.pilot[PPM_PILOT_ROWS[i].key]||'':''}</td></tr>`).join('')}</table>
    <h3>2.1 Fabric Details</h3><table><tr>${PPM_FABRIC_COLS.map(c=>`<th>${c.label}</th>`).join('')}</tr>
      ${d.fabrics.map(f=>`<tr>${PPM_FABRIC_COLS.map(c=>`<td>${f[c.key]||''}</td>`).join('')}</tr>`).join('')}</table>
    <h3>2.2 Fabric Inspection / 2.3 Shrinkage</h3><table><tr><th>Check</th><th>Result</th><th>Shrinkage</th><th>Value</th></tr>
      ${PPM_FAB_INSPECTION.map((f,i)=>`<tr><td>${f}</td><td>${d.fabInspection[f]||''}</td>
        <td>${PPM_SHRINKAGE[i]||''}</td><td>${PPM_SHRINKAGE[i]?d.shrinkage[PPM_SHRINKAGE[i]]||'':''}</td></tr>`).join('')}</table>
    <h3>4. Sample Approvals</h3><table><tr>${PPM_SAMPLE_APPROVALS.map(s=>`<th>${s}</th>`).join('')}</tr>
      <tr>${PPM_SAMPLE_APPROVALS.map(s=>`<td>${d.sampleApprovals[s]||''}</td>`).join('')}</tr></table>
    <p><b>Sample comments:</b> ${d.sampleComments||''}</p>
    <h3>9.2 CTQ / CTS / CTP</h3>
    ${['ctq','cts','ctp'].map(k=>`<b>${k.toUpperCase()}</b><table><tr><th>Attribute</th><th>Risk</th><th>Action</th><th>Responsible</th></tr>
      ${(d[k]||[]).map(r=>`<tr><td>${r.attribute||''}</td><td>${r.risk||''}</td><td>${r.action||''}</td><td>${r.responsible||''}</td></tr>`).join('')}</table>`).join('')}
    <h3>12. Home Test Report</h3><table><tr><th>Group</th><th>Criteria</th><th>Required</th><th>Value</th></tr>
      ${PPM_HOME_TESTS.flatMap(g=>g.rows.map(r=>`<tr><td>${g.group}</td><td>${r.criteria}</td><td>${r.required}</td>
        <td>${d.homeTests[g.group+'|'+r.criteria]||''}</td></tr>`)).join('')}</table>
    <h3>13. General Comments / Size Set Comments</h3><p>${(d.generalComments||'').replace(/\n/g,'<br/>')}</p>
    <table class="sign"><tr><th>Prepared By</th><th>Quality Manager</th><th>Production Manager</th><th>Approved by (GPQ Account Head)</th></tr><tr><td>${currentUser.name}</td><td></td><td></td><td></td></tr></table>`);

  return (
    <div>
      <FormatHeader code="PPM" po={po} />
      <div style={{ border:`1px solid ${T.border}`, borderTop:'none', borderRadius:'0 0 8px 8px', padding:16, background:'white' }}>
        <SubHead n="1." title="Merchandise & Meeting Details" />
        <Grid cols={4}>
          <Field label="PPM Date" type="date" value={d.meta.ppmDate} onChange={e=>setIn('meta','ppmDate',e.target.value)} />
          <Field label="Order Qty" type="number" value={d.meta.orderQty} onChange={e=>setIn('meta','orderQty',e.target.value)} />
          <Field label="Description" value={d.meta.description} onChange={e=>setIn('meta','description',e.target.value)} />
          <Field label="Fabrication" value={d.meta.fabrication} onChange={e=>setIn('meta','fabrication',e.target.value)} />
          <Field label="Merchandiser" value={d.meta.merchandiser} onChange={e=>setIn('meta','merchandiser',e.target.value)} />
          <Field label="Unit" value={d.meta.unit} onChange={e=>setIn('meta','unit',e.target.value)} />
          <Field label="Line" value={d.meta.line} onChange={e=>setIn('meta','line',e.target.value)} />
          <Field label="Ex-Factory Date" type="date" value={d.meta.exFactory} onChange={e=>setIn('meta','exFactory',e.target.value)} />
        </Grid>

        <Collapse title="1. Participants Attendance" defaultOpen
          badge={`${Object.values(d.participants).filter(Boolean).length}/${PPM_PARTICIPANTS.length} filled`}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10 }}>
            {PPM_PARTICIPANTS.map(p => (
              <Field key={p} label={p} value={d.participants[p]||''} onChange={e=>setIn('participants',p,e.target.value)} placeholder="Name" />
            ))}
          </div>
        </Collapse>

        <Collapse title="1.1 Production Plan & Inspection Milestones">
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
            <div>
              <b style={{ fontSize:11 }}>Section Start / End</b>
              {PPM_PLAN_ROWS.map(r=>(
                <div key={r.key} style={{ display:'flex', gap:8, alignItems:'center', marginTop:6 }}>
                  <span style={{ width:80, fontSize:11 }}>{r.label}</span>
                  <input type="date" value={d.plan[r.key+'_start']||''} onChange={e=>setIn('plan',r.key+'_start',e.target.value)}
                    style={{ flex:1, padding:5, border:'1px solid #cbd5e1', borderRadius:4, fontSize:11 }} />
                  <input type="date" value={d.plan[r.key+'_end']||''} onChange={e=>setIn('plan',r.key+'_end',e.target.value)}
                    style={{ flex:1, padding:5, border:'1px solid #cbd5e1', borderRadius:4, fontSize:11 }} />
                </div>
              ))}
            </div>
            <div>
              <b style={{ fontSize:11 }}>Pilot Run / Inspection Stages</b>
              {PPM_PILOT_ROWS.map(r=>(
                <div key={r.key} style={{ display:'flex', gap:8, alignItems:'center', marginTop:6 }}>
                  <span style={{ flex:1, fontSize:11 }}>{r.label}</span>
                  <input type="date" value={d.pilot[r.key]||''} onChange={e=>setIn('pilot',r.key,e.target.value)}
                    style={{ width:140, padding:5, border:'1px solid #cbd5e1', borderRadius:4, fontSize:11 }} />
                </div>
              ))}
            </div>
          </div>
        </Collapse>

        <Collapse title="2. Fabric Section — details, inspection & shrinkage">
          <SubHead n="2.1" title="Fabric Details" />
          <DynamicTable columns={PPM_FABRIC_COLS} data={d.fabrics} setData={v=>set('fabrics',v)}
            onAdd={()=>set('fabrics',[...d.fabrics,{}])} dense />
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
            <div>
              <SubHead n="2.2" title="Fabric Inspection Details" />
              {PPM_FAB_INSPECTION.map(f=>(
                <Grid key={f} cols={3} gap={6}>
                  <span style={{ fontSize:11, paddingTop:8 }}>{f}</span>
                  <Field label="Pass / Reject" value={d.fabInspection[f+'_result']||''} onChange={e=>setIn('fabInspection',f+'_result',e.target.value)} options={['Pass','Reject']} />
                  <Field label="Remarks" value={d.fabInspection[f]||''} onChange={e=>setIn('fabInspection',f,e.target.value)} />
                </Grid>
              ))}
            </div>
            <div>
              <SubHead n="2.3" title="Fabric Shrinkage" />
              {PPM_SHRINKAGE.map(s=>(
                <Field key={s} label={s} value={d.shrinkage[s]||''} onChange={e=>setIn('shrinkage',s,e.target.value)} placeholder="%" />
              ))}
            </div>
          </div>
        </Collapse>

        <Collapse title="3. Trims Section — thread details & trim status">
          <SubHead n="3.1" title="Thread / SPI Details" />
          <DynamicTable columns={PPM_THREAD_COLS} data={d.threads} setData={v=>set('threads',v)}
            onAdd={()=>set('threads',[...d.threads,{}])} dense />
          <SubHead n="3.2" title="Trims Status" />
          <Grid cols={2}>
            <Field label="All Trims In-House" value={d.trimsInHouse} onChange={e=>set('trimsInHouse',e.target.value)} options={['Yes','No']} />
            <Field label="Trim Card Approval" value={d.trimCardApproval} onChange={e=>set('trimCardApproval',e.target.value)} options={['Yes','No']} />
          </Grid>
          <DynamicTable columns={[
            { label:'Trims Item', key:'item' }, { label:'Potential Problems', key:'problem', type:'textarea' },
            { label:'Action Date', key:'actionDate', type:'date' }, { label:'Responsible', key:'responsible' },
            { label:'Remarks', key:'remarks' }]}
            data={d.trimIssues} setData={v=>set('trimIssues',v)} onAdd={()=>set('trimIssues',[...d.trimIssues,{}])} dense />
        </Collapse>

        <Collapse title="4. Sample Room — approvals & pattern release">
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10 }}>
            {PPM_SAMPLE_APPROVALS.map(s=>(
              <div key={s} style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                fontSize:11.5, padding:'6px 0', borderBottom:'1px solid #f1f5f9' }}>
                <span>{s}</span>
                <TriState value={d.sampleApprovals[s]||''} onChange={v=>setIn('sampleApprovals',s,v)} options={['Yes','No']} />
              </div>
            ))}
          </div>
          <Field label="4.2 Approved Sample Comments" type="textarea" value={d.sampleComments} onChange={e=>set('sampleComments',e.target.value)} />
          <Grid cols={4}>
            <Field label="Patterns Released to Cutting" value={d.patternsReleased} onChange={e=>set('patternsReleased',e.target.value)} options={['Yes','No']} />
            <Field label="Date" type="date" value={d.patternDate} onChange={e=>set('patternDate',e.target.value)} />
            <Field label="If Not — Action Date" type="date" value={d.patternAction} onChange={e=>set('patternAction',e.target.value)} />
            <Field label="Responsible" value={d.patternResponsible} onChange={e=>set('patternResponsible',e.target.value)} />
          </Grid>
        </Collapse>

        <Collapse title="5. Cutting Section — layering, fusing, numbering, bundling">
          <Grid cols={4}>
            <Field label="PCD" value={d.cutting.pcd} onChange={e=>setIn('cutting','pcd',e.target.value)} />
            <Field label="Consumption" value={d.cutting.consumption} onChange={e=>setIn('cutting','consumption',e.target.value)} />
            <Field label="Yds / Doz" value={d.cutting.ydsDoz} onChange={e=>setIn('cutting','ydsDoz',e.target.value)} />
            <Field label="Special Marker Advice" value={d.cutting.specialMarker} onChange={e=>setIn('cutting','specialMarker',e.target.value)} />
            <Field label="Cutting Start Date" type="date" value={d.cutting.startDate} onChange={e=>setIn('cutting','startDate',e.target.value)} />
            <Field label="Booking Qty" type="number" value={d.cutting.bookingQty} onChange={e=>setIn('cutting','bookingQty',e.target.value)} />
            <Field label="Possible Input Date" type="date" value={d.cutting.inputDate} onChange={e=>setIn('cutting','inputDate',e.target.value)} />
            <Field label="Actual Qty" type="number" value={d.cutting.actualQty} onChange={e=>setIn('cutting','actualQty',e.target.value)} />
          </Grid>
          <div style={{ background:'#fffbeb', border:'1px solid #fde68a', borderRadius:6, padding:10, fontSize:10.5, color:'#92400e', margin:'8px 0' }}>
            Cutting QC must verify all rolls & markers · 1 pc mini marker for every cutter · problematic rolls must carry proper identification.
          </div>
          <SubHead n="5.2" title="Layering Controls" />
          <CheckList items={PPM_LAYERING} values={d.layering} onChange={(k,v)=>setIn('layering',k,v)} cols={2} options={['Yes','No','NA']} />
          <SubHead n="5.3" title="Fusing / Interlining" />
          <DynamicTable columns={PPM_FUSING_COLS} data={d.fusing} setData={v=>set('fusing',v)} onAdd={()=>set('fusing',[...d.fusing,{}])} dense />
          <SubHead n="5.4" title="Numbering" />
          <Grid cols={4}>
            <Field label="Parts Not To Be Numbered" value={d.numbering.partsNotNumbered} onChange={e=>setIn('numbering','partsNotNumbered',e.target.value)} />
            <Field label="Numbering Positions" value={d.numbering.positions} onChange={e=>setIn('numbering','positions',e.target.value)} />
            <Field label="Reverse Side Big Sticker" value={d.numbering.reverseSticker} onChange={e=>setIn('numbering','reverseSticker',e.target.value)} options={['Yes','No']} />
            <Field label="Refer Numbering Mini Marker" value={d.numbering.miniMarker} onChange={e=>setIn('numbering','miniMarker',e.target.value)} options={['Yes','No']} />
          </Grid>
          <SubHead n="5.5" title="Bundling" />
          <Grid cols={3}>
            <Field label="Bundle Size — Standard" value={d.bundling.standard} onChange={e=>setIn('bundling','standard',e.target.value)} />
            <Field label="Bundle Size — Required" value={d.bundling.required} onChange={e=>setIn('bundling','required',e.target.value)} />
            <Field label="Special Bundling Requirements" value={d.bundling.special} onChange={e=>setIn('bundling','special',e.target.value)} />
          </Grid>
        </Collapse>

        <Collapse title="6. Production Department — layout, manpower, irreversible items">
          <Field label="6.1 Line Construction Details / Difficulties (during size set) — main bottleneck points"
            type="textarea" value={d.lineDifficulties} onChange={e=>set('lineDifficulties',e.target.value)} />
          <SubHead n="6.2" title="Production Line Layout — machine quantity" />
          <div style={{ display:'grid', gridTemplateColumns:'repeat(6,1fr)', gap:8 }}>
            {PPM_MACHINE_TYPES.map(m=>(
              <Field key={m} label={m} type="number" value={d.machines[m]||''} onChange={e=>setIn('machines',m,e.target.value)} />
            ))}
          </div>
          <Grid cols={4}>
            <Field label="Operators / Line" type="number" value={d.manpower.operatorsPerLine} onChange={e=>setIn('manpower','operatorsPerLine',e.target.value)} />
            <Field label="Total Manpower / Line" type="number" value={d.manpower.totalPerLine} onChange={e=>setIn('manpower','totalPerLine',e.target.value)} />
            <Field label="Target Production / Line" type="number" value={d.manpower.targetPerLine} onChange={e=>setIn('manpower','targetPerLine',e.target.value)} />
            <Field label="Target Production / Day" type="number" value={d.manpower.targetPerDay} onChange={e=>setIn('manpower','targetPerDay',e.target.value)} />
          </Grid>
          <SubHead n="6.3" title="Irreversible Item Attachment Details" />
          <DynamicTable columns={[
            { label:'Name of Item', key:'item' }, { label:'Req. Qty', key:'qty' },
            { label:'Size', key:'size' }, { label:'Attached Position', key:'position' }]}
            data={d.irreversible} setData={v=>set('irreversible',v)} onAdd={()=>set('irreversible',[...d.irreversible,{}])} dense />
        </Collapse>

        <Collapse title="7-8. Washing & Mechanical Department">
          <Grid cols={3}>
            <Field label="Wash" value={d.washing.wash} onChange={e=>setIn('washing','wash',e.target.value)} />
            <Field label="Shade Band Approval" value={d.washing.shadeBand} onChange={e=>setIn('washing','shadeBand',e.target.value)} options={['Yes','No']} />
            <Field label="If Not — Reason" value={d.washing.reason} onChange={e=>setIn('washing','reason',e.target.value)} />
            <Field label="Action Date" type="date" value={d.washing.actionDate} onChange={e=>setIn('washing','actionDate',e.target.value)} />
            <Field label="Responsible" value={d.washing.responsible} onChange={e=>setIn('washing','responsible',e.target.value)} />
          </Grid>
          <Field label="Special Instruction for Sewing" type="textarea" value={d.washing.sewingInstruction} onChange={e=>setIn('washing','sewingInstruction',e.target.value)} />
          <Grid cols={4}>
            <Field label="Workaid Used (folder, gauge etc.)" value={d.mechanical.workaid} onChange={e=>setIn('mechanical','workaid',e.target.value)} />
            <Field label="Folder Will Be Used" value={d.mechanical.folder} onChange={e=>setIn('mechanical','folder',e.target.value)} />
            <Field label="Machine Shortages" value={d.mechanical.shortages} onChange={e=>setIn('mechanical','shortages',e.target.value)} />
            <Field label="Responsible" value={d.mechanical.responsible} onChange={e=>setIn('mechanical','responsible',e.target.value)} />
          </Grid>
        </Collapse>

        <Collapse title="9. Quality Assurance — quality points, CTQ / CTS / CTP" defaultOpen>
          <SubHead n="9.1" title="Important Quality Points" />
          <DynamicTable columns={[
            { label:'Check Point', key:'checkPoint', type:'textarea' },
            { label:'100% Goods Inspection', key:'fullInspection', type:'select', options:['Yes','No'] },
            { label:'Focus Point', key:'focusPoint', type:'textarea' }]}
            data={d.qualityPoints} setData={v=>set('qualityPoints',v)} onAdd={()=>set('qualityPoints',[...d.qualityPoints,{}])} dense />
          <Field label="Percentage (%) of Optimum Quality Level Target" value={d.optimumTarget} onChange={e=>set('optimumTarget',e.target.value)} style={{ maxWidth:300 }} />
          {[['ctq','9.2 CTQ — Critical to Quality'],['cts','CTS — Critical to Safety'],['ctp','CTP — Critical to Process']].map(([k,label])=>(
            <div key={k}>
              <SubHead n="" title={label} />
              <DynamicTable columns={PPM_CT_COLS} data={d[k]} setData={v=>set(k,v)} onAdd={()=>set(k,[...d[k],{}])} dense />
            </div>
          ))}
        </Collapse>

        <Collapse title="10. Finishing — pressing, folding & packing">
          <Grid cols={2}>
            <Field label="Pressing Method" value={d.pressing.method} onChange={e=>setIn('pressing','method',e.target.value)}
              options={['Vapour Pressing','Hard Pressing','No Pressing','Others']} />
            <Field label="Comments for Pressing" value={d.pressing.comments} onChange={e=>setIn('pressing','comments',e.target.value)} />
            <Field label="Folding Method" value={d.folding.method} onChange={e=>setIn('folding','method',e.target.value)} />
            <Field label="Folding Comments" value={d.folding.comments} onChange={e=>setIn('folding','comments',e.target.value)} />
            <Field label="Packing Method" value={d.packing.method} onChange={e=>setIn('packing','method',e.target.value)} />
            <Field label="Packing Comments" value={d.packing.comments} onChange={e=>setIn('packing','comments',e.target.value)} />
          </Grid>
          <SubHead n="10.2" title="Packing Accessories" />
          <CheckList items={PPM_PACK_ACCESSORIES} values={d.packAccessories} onChange={(k,v)=>setIn('packAccessories',k,v)} cols={2} options={['OK','N/OK']} />
        </Collapse>

        <Collapse title="11-12. Historical Background & Home Test Report">
          <Grid cols={4}>
            <Field label="Production Unit" value={d.history.unit} onChange={e=>setIn('history','unit',e.target.value)} />
            <Field label="Fabrication" value={d.history.fabrication} onChange={e=>setIn('history','fabrication',e.target.value)} />
            <Field label="Thread" value={d.history.thread} onChange={e=>setIn('history','thread',e.target.value)} />
            <Field label="Washing" value={d.history.washing} onChange={e=>setIn('history','washing',e.target.value)} />
            <Field label="Historical DHU %" value={d.history.dhu} onChange={e=>setIn('history','dhu',e.target.value)} />
            <Field label="Historical Efficiency %" value={d.history.eff} onChange={e=>setIn('history','eff',e.target.value)} />
          </Grid>
          <Field label="Top 3 Defects of Previous FRI" type="textarea" value={d.history.topDefects} onChange={e=>setIn('history','topDefects',e.target.value)} />
          {PPM_HOME_TESTS.map(g=>(
            <div key={g.group} style={{ marginTop:8 }}>
              <b style={{ fontSize:11, color:T.textSub }}>{g.group}</b>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10 }}>
                {g.rows.map(r=>(
                  <div key={r.criteria} style={{ display:'flex', gap:6, alignItems:'center', fontSize:11 }}>
                    <span style={{ flex:1 }}>{r.criteria} <span style={{ color:T.textSub }}>(req {r.required})</span></span>
                    <input value={d.homeTests[g.group+'|'+r.criteria]||''} onChange={e=>setIn('homeTests',g.group+'|'+r.criteria,e.target.value)}
                      placeholder="value" style={{ width:70, padding:4, border:'1px solid #cbd5e1', borderRadius:4, fontSize:11 }} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </Collapse>

        <Field label="13. General Comments / Size Set Comments" type="textarea" value={d.generalComments} onChange={e=>set('generalComments',e.target.value)} />
        <div style={{ display:'flex', gap:10, marginTop:12 }}>
          <Btn label="Save PPM Minutes" color={T.success} onClick={save} />
          <Btn label="🖨 Print / PDF" outline onClick={print} />
          {onCancel && <Btn label="Cancel" color="#64748b" onClick={onCancel} />}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  J-02 · MANUFACTURING CONTROL PLAN
// ═══════════════════════════════════════════════════════════════════
export function MCPForm({ po, currentUser, existing, onSave, onCancel, variant = 'Standard' }) {
  const base = variant === 'Wash' ? MCP_WASH_STEPS : MCP_STEPS;
  const [meta, setMeta] = useState(existing?.data?.meta || {
    date: today(), unit:'', productCategory:'', item:'', season:'',
    processesInvolved:{ Washing:'', Printing:'', Embroidery:'' },
    fabricTypes:{ Woven:'', Denim:'', Stretch:'', Knit:'' },
  });
  const [steps, setSteps] = useState(existing?.data?.steps ||
    base.map(s => ({ step:s.step, rows:s.rows.map(r=>({ ...r })) })));

  const setRows = (i, rows) => setSteps(p => p.map((s,ix)=> ix===i ? { ...s, rows } : s));

  const save = () => onSave({
    id: existing?.id || uid(), format:'MCP', variant, iso: fmt('MCP').iso, poNo: po?.poNo, styleNo: po?.styleNo,
    supplier: po?.supplier, brand: po?.brand, date: meta.date, preparedBy: currentUser.name,
    status:'Completed', data:{ meta, steps },
  });

  const print = () => printHTML(`MCP ${po?.poNo||''}`, `
    <h1>TOQUM · Manufacturing Control Plan ${variant==='Wash'?'(Wash)':''}</h1>
    <div>Format ${fmt('MCP').iso} v${fmt('MCP').version} — Process Monitoring, Inspection & Testing</div>
    <table><tr><td><b>Date:</b> ${meta.date}</td><td><b>Buyer:</b> ${po?.brand||''}</td>
      <td><b>Style:</b> ${po?.styleNo||''}</td><td><b>Season:</b> ${meta.season}</td></tr>
      <tr><td><b>Unit:</b> ${meta.unit}</td><td><b>Product Category:</b> ${meta.productCategory}</td>
      <td><b>Item:</b> ${meta.item}</td><td><b>PO:</b> ${po?.poNo||''}</td></tr></table>
    ${steps.map(s=>`<h3>Process Step: ${s.step}</h3>
      <table><tr>${MCP_COLS.map(c=>`<th>${c.label}</th>`).join('')}</tr>
      ${s.rows.map(r=>`<tr>${MCP_COLS.map(c=>`<td>${(r[c.key]||'').toString().replace(/\n/g,'<br/>')}</td>`).join('')}</tr>`).join('')}
      </table>`).join('')}
    <table class="sign"><tr><th>Prepared By</th><th>Quality Manager</th><th>Production Manager</th></tr>
      <tr><td>${currentUser.name}</td><td></td><td></td></tr></table>`);

  return (
    <div>
      <FormatHeader code="MCP" po={po} extra={<><br/>Variant: <b style={{color:'white'}}>{variant}</b></>} />
      <div style={{ border:`1px solid ${T.border}`, borderTop:'none', borderRadius:'0 0 8px 8px', padding:16, background:'white' }}>
        <Grid cols={4}>
          <Field label="Date" type="date" value={meta.date} onChange={e=>setMeta({...meta,date:e.target.value})} />
          <Field label="Unit" value={meta.unit} onChange={e=>setMeta({...meta,unit:e.target.value})} />
          <Field label="Product Category" value={meta.productCategory} onChange={e=>setMeta({...meta,productCategory:e.target.value})} placeholder="e.g. Woven" />
          <Field label="Item" value={meta.item} onChange={e=>setMeta({...meta,item:e.target.value})} placeholder="e.g. Mens" />
          <Field label="Season" value={meta.season} onChange={e=>setMeta({...meta,season:e.target.value})} />
        </Grid>
        <div style={{ display:'flex', gap:24, flexWrap:'wrap', marginBottom:10 }}>
          <div>
            <b style={{ fontSize:11, color:T.textSub }}>Processes Involved</b>
            <div style={{ display:'flex', gap:12, marginTop:4 }}>
              {['Washing','Printing','Embroidery'].map(p=>(
                <label key={p} style={{ fontSize:11.5 }}>
                  <input type="checkbox" checked={!!meta.processesInvolved[p]}
                    onChange={e=>setMeta({...meta,processesInvolved:{...meta.processesInvolved,[p]:e.target.checked?'Yes':''}})} /> {p}
                </label>
              ))}
            </div>
          </div>
          <div>
            <b style={{ fontSize:11, color:T.textSub }}>Types of Fabric</b>
            <div style={{ display:'flex', gap:12, marginTop:4 }}>
              {['Woven','Denim','Stretch','Knit'].map(p=>(
                <label key={p} style={{ fontSize:11.5 }}>
                  <input type="checkbox" checked={!!meta.fabricTypes[p]}
                    onChange={e=>setMeta({...meta,fabricTypes:{...meta.fabricTypes,[p]:e.target.checked?'Yes':''}})} /> {p}
                </label>
              ))}
            </div>
          </div>
        </div>
        <div style={{ fontSize:11, color:T.textSub, background:'#f8fafc', padding:'8px 10px', borderRadius:6, marginBottom:10 }}>
          The control plan below is pre-loaded from the standard MCP. Edit any cell for this style, add rows, or delete
          controls that don't apply. Everything saved here becomes the reference the QA follows during inspection.
        </div>
        {steps.map((s,i)=>(
          <Collapse key={s.step} title={`Process Step: ${s.step}`} badge={`${s.rows.length} controls`} defaultOpen={i===0}>
            <DynamicTable columns={MCP_COLS} data={s.rows} setData={rows=>setRows(i,rows)}
              onAdd={()=>setRows(i,[...s.rows,{ applicability:'Mandatory' }])} dense />
          </Collapse>
        ))}
        <div style={{ display:'flex', gap:10, marginTop:12 }}>
          <Btn label="Save MCP" color={T.success} onClick={save} />
          <Btn label="🖨 Print / PDF" outline onClick={print} />
          {onCancel && <Btn label="Cancel" color="#64748b" onClick={onCancel} />}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  J-03 · RISK ASSESSMENT AND PREVENTIVE ACTION
// ═══════════════════════════════════════════════════════════════════
export function RiskForm({ po, currentUser, existing, onSave, onCancel }) {
  const [meta, setMeta] = useState(existing?.data?.meta || {
    date: today(), colours:(po?.matrix||[]).filter(m=>m.color).length || '',
    orderQty: po?.qty||'', styleDesc:'', fabrication:'',
  });
  const [rows, setRows] = useState(existing?.data?.rows || []);
  const openCount = rows.filter(r=>r.status!=='Closed').length;

  const save = () => onSave({
    id: existing?.id || uid(), format:'RISK', iso: fmt('RISK').iso, poNo: po?.poNo, styleNo: po?.styleNo,
    supplier: po?.supplier, brand: po?.brand, date: meta.date, preparedBy: currentUser.name,
    status: openCount ? 'In Progress' : 'Completed', openRisks: openCount, data:{ meta, rows },
  });

  const print = () => printHTML(`Risk Assessment ${po?.poNo||''}`, `
    <h1>TOQUM · Risk Assessment and Preventive Action</h1>
    <div>Format ${fmt('RISK').iso} v${fmt('RISK').version}</div>
    <table><tr><td><b>Buyer:</b> ${po?.brand||''}</td><td><b>Colour(s):</b> ${meta.colours}</td><td><b>Date:</b> ${meta.date}</td></tr>
      <tr><td><b>Style:</b> ${po?.styleNo||''}</td><td><b>Order Quantity:</b> ${num(meta.orderQty).toLocaleString()}</td><td><b>PO:</b> ${po?.poNo||''}</td></tr>
      <tr><td><b>Style Desc:</b> ${meta.styleDesc}</td><td colspan="2"><b>Fabrication:</b> ${meta.fabrication}</td></tr></table>
    <h3>Department-wise Call Out & Recap (Post PTR Meeting)</h3>
    <table><tr>${RISK_COLS.map(c=>`<th>${c.label}</th>`).join('')}<th>Signature</th></tr>
      ${rows.map(r=>`<tr>${RISK_COLS.map(c=>`<td>${(r[c.key]||'').toString().replace(/\n/g,'<br/>')}</td>`).join('')}<td></td></tr>`).join('')}</table>
    <table class="sign"><tr><th>Section Head</th><th>Production Head</th><th>Quality Head</th></tr><tr><td></td><td></td><td></td></tr></table>`);

  return (
    <div>
      <FormatHeader code="RISK" po={po} />
      <div style={{ border:`1px solid ${T.border}`, borderTop:'none', borderRadius:'0 0 8px 8px', padding:16, background:'white' }}>
        <Grid cols={4}>
          <Field label="Date" type="date" value={meta.date} onChange={e=>setMeta({...meta,date:e.target.value})} />
          <Field label="No. of Colours" value={meta.colours} onChange={e=>setMeta({...meta,colours:e.target.value})} />
          <Field label="Order Quantity" type="number" value={meta.orderQty} onChange={e=>setMeta({...meta,orderQty:e.target.value})} />
          <Field label="Style Description" value={meta.styleDesc} onChange={e=>setMeta({...meta,styleDesc:e.target.value})} />
          <Field label="Fabrication" value={meta.fabrication} onChange={e=>setMeta({...meta,fabrication:e.target.value})} />
        </Grid>
        <SubHead n="" title="Department-wise Respective Call Out & Recap (Post PTR Meeting)" />
        <DynamicTable columns={RISK_COLS} data={rows} setData={setRows}
          onAdd={()=>setRows([...rows,{ status:'Open' }])} dense />
        <div style={{ fontSize:11.5, color: openCount?T.danger:T.success, fontWeight:700, marginBottom:10 }}>
          {openCount ? `⚠ ${openCount} preventive action(s) still open — these feed the supplier risk KPI.` : '✓ All preventive actions closed.'}
        </div>
        <div style={{ display:'flex', gap:10 }}>
          <Btn label="Save Risk Assessment" color={T.success} onClick={save} />
          <Btn label="🖨 Print / PDF" outline onClick={print} />
          {onCancel && <Btn label="Cancel" color="#64748b" onClick={onCancel} />}
        </div>
      </div>
    </div>
  );
}

// ── Production Status Panel ─────────────────────────────────────
// Shows department-wise production vs PO qty vs offered, rendered before inspection form
function ProductionStatusPanel({ po, styleData, checkInData, h }) {
  if (!po?.styleNo && !styleData?.styleNo) return null;
  const matrix   = po?.matrix || [];
  const poQty    = num(po?.qty);
  const prodLogs = (checkInData?.productionLogs||[]).filter(l=>l.poNo===po?.poNo) || [];
  const STAGES   = ['Cutting','Sewing','Washing','Finishing','Packing'];
  const stageTotals = {};
  STAGES.forEach(s=>{ stageTotals[s]=0; });
  prodLogs.forEach(l=>{ if(stageTotals[l.stage]!==undefined) stageTotals[l.stage]+=num(l.qty); });
  const byCS = {};
  matrix.forEach(m=>{ const k=`${m.color}|${m.size}`; if(!byCS[k]) byCS[k]={...m,poQty:num(m.qty),packed:0}; });
  prodLogs.filter(l=>l.stage==='Packing').forEach(l=>{ const k=`${l.color}|${l.size}`; if(byCS[k]) byCS[k].packed+=num(l.qty); });
  const totalPacked = Object.values(byCS).reduce((a,r)=>a+r.packed,0);
  const offeredQty  = num(h?.visualCheckQty||h?.ordQty||poQty);

  return (
    <div style={{ marginBottom:14 }}>
      <div style={{ background:'linear-gradient(90deg,#12161c,#1a2236)', color:'white',
        borderRadius:'8px 8px 0 0', padding:'10px 14px', fontSize:11,
        fontWeight:800, textTransform:'uppercase', letterSpacing:'0.06em' }}>
        📊 Production Status — PO {po?.poNo} · {po?.styleNo} · {po?.supplier}
      </div>
      <div style={{ background:'#f8fafc', border:`1px solid ${T.border}`, borderTop:'none',
        padding:'8px 14px', fontSize:11.5, display:'grid',
        gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))', gap:'3px 16px' }}>
        {[['Brand',po?.brand],['Fabrication',styleData?.fabrication||styleData?.product],
          ['GSM',styleData?.gsm],['Composition',styleData?.composition],
          ['Season',styleData?.season],['Ex-Factory',po?.exFactory]
        ].filter(([,v])=>v).map(([k,v])=>(
          <div key={k}><span style={{color:T.textSub}}>{k}: </span><b>{v}</b></div>
        ))}
      </div>
      {/* Stage progress */}
      <div style={{ border:`1px solid ${T.border}`, borderTop:'none', padding:'10px 14px', background:'white' }}>
        <div style={{ fontSize:10.5, fontWeight:800, textTransform:'uppercase',
          letterSpacing:'0.05em', color:T.textSub, marginBottom:8 }}>
          Department-wise Production vs PO Qty ({poQty.toLocaleString()} pcs)
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:8 }}>
          {STAGES.map(stage=>{
            const qty = stageTotals[stage]||0;
            const pct = poQty ? Math.min(100,Math.round(qty/poQty*100)) : 0;
            const col = pct>=100?T.success:pct>=80?T.warning:T.danger;
            return (
              <div key={stage} style={{ background:'#f8fafc', borderRadius:6,
                padding:'8px 10px', border:`1px solid ${T.border}` }}>
                <div style={{ fontSize:9.5, color:T.textSub, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:4 }}>{stage}</div>
                <div style={{ fontSize:16, fontWeight:900, color:col }}>{qty.toLocaleString()}</div>
                <div style={{ marginTop:4, height:4, background:'#e6e9ef', borderRadius:2 }}>
                  <div style={{ height:'100%', width:`${pct}%`, background:col, borderRadius:2 }}/>
                </div>
                <div style={{ fontSize:9.5, color:col, fontWeight:700, marginTop:2 }}>{pct}%</div>
              </div>
            );
          })}
        </div>
      </div>
      {/* PO vs Packed vs Offered breakdown */}
      {matrix.length > 0 && (
        <div style={{ border:`1px solid ${T.border}`, borderTop:'none', background:'white', overflowX:'auto' }}>
          <div style={{ padding:'8px 14px', fontSize:10.5, fontWeight:800,
            textTransform:'uppercase', letterSpacing:'0.05em', color:T.textSub,
            borderBottom:`1px solid ${T.border}`, background:'#f8fafc' }}>
            Destination / Color / Size — PO vs Packed vs Offered for Inspection
          </div>
          <table style={{ borderCollapse:'collapse', fontSize:11.5, width:'100%', minWidth:600 }}>
            <thead><tr style={{ background:'#1f1f1f', color:'white' }}>
              {['Color','Country','Size','PO Qty','Packed','Offered','Variance','%'].map(h=>(
                <th key={h} style={{ padding:'6px 10px', textAlign:'left', fontWeight:700 }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>{Object.values(byCS).map((r,i)=>{
              const offered = offeredQty&&poQty ? Math.round(offeredQty*(r.poQty/poQty)) : 0;
              const diff    = r.packed - r.poQty;
              const pct     = r.poQty ? Math.round(r.packed/r.poQty*100) : 0;
              const ok      = r.packed >= r.poQty;
              return (
                <tr key={i} style={{ borderBottom:`1px solid ${T.border}`,
                  background:r.packed===0?`${T.danger}06`:ok?`${T.success}06`:`${T.warning}06` }}>
                  <td style={{ padding:'5px 10px', fontWeight:600 }}>{r.color||'—'}</td>
                  <td style={{ padding:'5px 10px' }}>{r.country||'—'}</td>
                  <td style={{ padding:'5px 10px' }}>{r.size||'—'}</td>
                  <td style={{ padding:'5px 10px', fontWeight:700 }}>{r.poQty.toLocaleString()}</td>
                  <td style={{ padding:'5px 10px', fontWeight:700,
                    color:ok?T.success:r.packed>0?T.warning:T.danger }}>{r.packed.toLocaleString()}</td>
                  <td style={{ padding:'5px 10px', fontWeight:700, color:T.primary }}>{offered.toLocaleString()}</td>
                  <td style={{ padding:'5px 10px', fontWeight:700, color:diff>=0?T.success:T.danger }}>
                    {diff>=0?'+':''}{diff.toLocaleString()}
                  </td>
                  <td style={{ padding:'5px 10px' }}>
                    <span style={{ fontWeight:800, color:pct>=100?T.success:pct>=50?T.warning:T.danger }}>{pct}%</span>
                  </td>
                </tr>
              );
            })}</tbody>
            <tfoot><tr style={{ background:'#f1f5f9', fontWeight:800, fontSize:12 }}>
              <td colSpan={3} style={{ padding:'7px 10px' }}>TOTAL</td>
              <td style={{ padding:'7px 10px' }}>{poQty.toLocaleString()}</td>
              <td style={{ padding:'7px 10px', color:totalPacked>=poQty?T.success:T.warning }}>{totalPacked.toLocaleString()}</td>
              <td style={{ padding:'7px 10px', color:T.primary }}>{offeredQty.toLocaleString()}</td>
              <td colSpan={2} style={{ padding:'7px 10px', color:totalPacked>=poQty?T.success:T.danger }}>
                {totalPacked>=poQty?'✓ FULL QTY PACKED':'⚠ PARTIAL'}
              </td>
            </tr></tfoot>
          </table>
          {totalPacked < poQty && (
            <div style={{ padding:'8px 14px', background:'#fff8e6',
              borderTop:`1px solid ${T.warning}`, fontSize:11.5, color:'#8a5a00' }}>
              ⚠ Only {totalPacked.toLocaleString()} of {poQty.toLocaleString()} pcs packed.
              Inspection is on partial quantity. QA can proceed or escalate for exception.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  J-04 · IN-LINE / PRE-FINAL INSPECTION REPORT
// ═══════════════════════════════════════════════════════════════════
export function InlineInspectionForm({ po, currentUser, defectCodes = [], existing, onSave, onCancel,
                                       gateBlocked, gateInfo, onRaiseException,
                                       styleData = {}, specData = {}, checkInData = null }) {

  // ── Pre-populate measurements from style's extracted spec sheet ──
  // QA NEVER enters spec values — they come from the style DB.
  // QA only enters ACTUAL measured values. Pass/fail is system-determined.
  const buildMeasRows = () => {
    if (existing?.measRows) return existing.measRows;
    const meas = styleData?.measurements || specData?.measurements;
    if (meas?.rows?.length) {
      return meas.rows.map(r => {
        const tolMinus = parseFloat(r.tol_minus || r.tolMinus || r.tolerance || 1.0);
        const tolPlus  = parseFloat(r.tol_plus  || r.tolPlus  || r.tolerance || 1.0);
        const specPerSize = (meas.sizes||[]).reduce((acc,s) => ({
          ...acc, [s]: (r[s] !== undefined && r[s] !== '') ? String(r[s]) : ''
        }), {});
        return {
          point: r.pom || r.point || r.description || '',
          tolMinus, tolPlus, tolerance: tolPlus,
          specPerSize, actuals: {}, result: '',
        };
      });
    }
    return STD_MEASUREMENT_POINTS.map(p => ({
      point:p.point, tolMinus:p.tolerance, tolPlus:p.tolerance,
      tolerance:p.tolerance, specPerSize:{}, actuals:{}, result:'',
    }));
  };

  const buildMeasSizes = () => {
    if (existing?.measSizes) return existing.measSizes;
    // Prefer sizes from the extracted spec sheet
    const meas = styleData?.measurements || specData?.measurements;
    if (meas?.sizes?.length) return meas.sizes;
    // Fall back to PO matrix sizes
    const poSizes = [...new Set((po?.matrix||[]).map(m=>m.size).filter(Boolean))];
    return poSizes.length ? poSizes.slice(0,8) : ['S','M','L','XL'];
  };
  const [h, setH] = useState(existing?.header || {
    technician: currentUser.name, date: today(), userType:'In-line', tod:'', section:'', unit:'', line:'',
    merchandiser:'', tech:'', packQty:'', ctnQty:'', ctnPickQty:'', ordQty: po?.qty||'', cutQty:'',
    sewingQty:'', finishingQty:'', visualCheckQty:'', measurementQty:'', colour:'', country:'',
    samplePick:'', aqlVisual:'2.5', aqlMeasurement:'4.0', fibre:'Cotton', foundMoisture:'', moistureNotes:'',
  });
  const [styling, setStyling] = useState(existing?.styling || {});
  const [checks, setChecks]   = useState(existing?.checks || {});
  const [defects, setDefects] = useState(existing?.defects || []);
  const [comments, setComments] = useState(existing?.comments || '');
  const [images, setImages]   = useState(existing?.imagesData || []);
  // Measurement spec sheet — pre-populated from style data
  const [measSizes, setMeasSizes] = useState(()=>buildMeasSizes());
  const [measRows, setMeasRows]   = useState(()=>buildMeasRows());
  const [measNotes, setMeasNotes] = useState(existing?.measNotes || '');

  const setH_ = (k,v) => setH(p=>({ ...p, [k]:v }));
  const lot = num(h.ordQty) || num(po?.qty);
  const planV = aqlPlan(lot, h.aqlVisual);
  const planM = aqlPlan(lot, h.aqlMeasurement);
  const totals = defects.reduce((a,d)=>({
    critical:a.critical+num(d.critical), major:a.major+num(d.major), minor:a.minor+num(d.minor) }),
    { critical:0, major:0, minor:0 });
  const verdict = totals.critical > 0 ? 'FAIL'
    : (totals.major + totals.minor) > planV.accept ? 'FAIL' : 'PASS';

  const addImages = (e) => {
    Array.from(e.target.files||[]).forEach(file => {
      const r = new FileReader();
      r.onload = () => setImages(im=>[...im,{ name:file.name, data:r.result, caption:'' }]);
      r.readAsDataURL(file);
    });
    e.target.value='';
  };

  // System-calculated measurement results (QA cannot override)
  const calcResult = (row) => {
    const hasActuals = measSizes.some(s => row.actuals[s] !== undefined && row.actuals[s] !== '');
    if (!hasActuals) return '';
    const failed = measSizes.some(s => {
      const spec = row.specPerSize?.[s];
      const act  = row.actuals[s];
      if (!spec || act === undefined || act === '') return false;
      const specN = parseFloat(spec), actN = parseFloat(act);
      const tolM  = parseFloat(row.tolMinus || row.tolerance || 1);
      const tolP  = parseFloat(row.tolPlus  || row.tolerance || 1);
      return !isNaN(specN) && !isNaN(actN) && (actN < specN - tolM || actN > specN + tolP);
    });
    return failed ? 'Fail' : 'Pass';
  };
  const measFails = measRows.filter(r => calcResult(r) === 'Fail').length;
  const measPass  = measRows.filter(r => calcResult(r) === 'Pass').length;

  const save = (result) => onSave({
    id: existing?.id || uid(), format:'INSP', iso: fmt('INSP').iso, poNo: po?.poNo, styleNo: po?.styleNo,
    supplier: po?.supplier, brand: po?.brand, stage: h.userType, date: h.date, qaMember: currentUser.name,
    header:h, styling, checks, defects, totals, comments, imagesData:images,
    measSizes, measRows, measNotes, measSummary:{ pass:measPass, fail:measFails, total:measRows.length },
    aql:{ visual:h.aqlVisual, measurement:h.aqlMeasurement, planV, planM },
    result, status:'Completed',
  });

  const print = () => printHTML(`Inspection ${po?.poNo||''}`, `
    <h1>TOQUM · In-line / Pre-Final Inspection Report</h1>
    <div>Format ${fmt('INSP').iso} v${fmt('INSP').version} · Technician: ${h.technician} · Date: ${h.date}</div>
    <table>
      <tr><td><b>User Type:</b> ${h.userType}</td><td><b>TOD:</b> ${h.tod}</td><td><b>Section:</b> ${h.section}</td>
          <td><b>Unit:</b> ${h.unit}</td><td><b>Line:</b> ${h.line}</td></tr>
      <tr><td><b>Merchandiser:</b> ${h.merchandiser}</td><td><b>Tech:</b> ${h.tech}</td><td><b>Pack Qty:</b> ${h.packQty}</td>
          <td colspan="2"><b>Final Inspection Result:</b> ${verdict}</td></tr>
      <tr><td><b>Buyer:</b> ${po?.brand||''}</td><td><b>Ord Qty:</b> ${h.ordQty}</td><td><b>Ctn Qty:</b> ${h.ctnQty}</td>
          <td><b>Critical:</b> ${totals.critical}</td><td><b>Major:</b> ${totals.major}</td></tr>
      <tr><td><b>Style:</b> ${po?.styleNo||''}</td><td><b>Cut Qty:</b> ${h.cutQty}</td><td><b>Ctn Pick Qty:</b> ${h.ctnPickQty}</td>
          <td><b>Minor:</b> ${totals.minor}</td><td><b>Acceptable:</b> ${planV.accept}</td></tr>
      <tr><td><b>Order No:</b> ${po?.poNo||''}</td><td><b>Sewing Qty:</b> ${h.sewingQty}</td><td><b>Visual Check Qty:</b> ${h.visualCheckQty}</td>
          <td colspan="2"><b>Defects Found:</b> ${totals.critical+totals.major+totals.minor}</td></tr>
      <tr><td><b>Colour:</b> ${h.colour}</td><td><b>Finishing Qty:</b> ${h.finishingQty}</td><td><b>Measurement Qty:</b> ${h.measurementQty}</td>
          <td><b>Country:</b> ${h.country}</td><td><b>Sample Pick:</b> ${h.samplePick}</td></tr>
      <tr><td><b>Visual AQL:</b> ${h.aqlVisual} (n=${planV.sampleSize}, Ac ${planV.accept} / Re ${planV.reject})</td>
          <td colspan="2"><b>Measurement AQL:</b> ${h.aqlMeasurement} (n=${planM.sampleSize}, Ac ${planM.accept} / Re ${planM.reject})</td>
          <td><b>Fibre:</b> ${h.fibre} (limit ${MOISTURE_LIMITS[h.fibre]||'—'})</td><td><b>Found Moisture:</b> ${h.foundMoisture}</td></tr>
    </table>
    ${h.moistureNotes?`<p><b>Notes on over-moisture:</b> ${h.moistureNotes}</p>`:''}
    <h3>Checkpoints</h3>
    <table><tr>${Object.keys(INSP_CHECK_SECTIONS).map(s=>`<th>${s}</th>`).join('')}</tr>
      ${Array.from({length: Math.max(...Object.values(INSP_CHECK_SECTIONS).map(v=>v.length))}).map((_,i)=>
        `<tr>${Object.entries(INSP_CHECK_SECTIONS).map(([sec,items])=>{
          const it = items[i];
          return `<td>${it ? `${it}: <b>${checks[sec+'|'+it]||'—'}</b>` : ''}</td>`; }).join('')}</tr>`).join('')}
    </table>
    <h3>Workmanship Findings & Defects</h3>
    <table><tr><th>S/N</th><th>Defect Code</th><th>Workmanship Finding / Defect</th><th>Critical</th><th>Major</th><th>Minor</th></tr>
      ${defects.map((d,i)=>`<tr><td>${i+1}</td><td>${d.code||''}</td><td>${d.description||''}</td>
        <td>${d.critical||0}</td><td>${d.major||0}</td><td>${d.minor||0}</td></tr>`).join('')}
      <tr><th colspan="3">Total Defects</th><th>${totals.critical}</th><th>${totals.major}</th><th>${totals.minor}</th></tr></table>
    <p><b>Comments:</b> ${(comments||'').replace(/\n/g,'<br/>')}</p>
    ${images.length?`<h3>Inspection Images</h3><div class="imgs">${images.map(i=>`<div><img src="${i.data}"/><div style="font-size:8px">${i.caption||i.name}</div></div>`).join('')}</div>`:''}
    <h3>Measurement Spec Sheet</h3>
    <table><tr><th>Measurement Point</th><th>Tolerance (±cm)</th><th>Spec</th>
      ${measSizes.map(s=>`<th>${s}</th>`).join('')}<th>Result</th></tr>
      ${measRows.map(r=>`<tr>
        <td>${r.point}</td><td style="text-align:center">±${r.tolerance}</td><td style="text-align:center">${r.spec||'—'}</td>
        ${measSizes.map(s=>`<td style="text-align:center;color:${r.result==='Fail'?'#b4232f':r.result==='Pass'?'#0a7d43':'inherit'}">${r.actuals[s]||'—'}</td>`).join('')}
        <td style="font-weight:700;color:${r.result==='Fail'?'#b4232f':r.result==='Pass'?'#0a7d43':'#676879'}">${r.result||'—'}</td>
      </tr>`).join('')}
    </table>
    <p><b>Measurement Summary:</b> ${measPass} Pass · ${measFails} Fail of ${measRows.length} points</p>
    ${measNotes?`<p><b>Measurement Notes:</b> ${measNotes.replace(/\n/g,'<br/>')}</p>`:''}
    <p class="note">Note: Inspection of the order does not relieve the factory from his responsibility for the quality of shipped goods.</p>
    <table class="sign"><tr><th>Prepared By</th><th>PM</th><th>QM</th><th>AGM / GM (Q.A)</th></tr>
      <tr><td>${h.technician}</td><td></td><td></td><td></td></tr></table>`);

  return (
    <div>
      <FormatHeader code="INSP" po={po} />
      <div style={{ border:`1px solid ${T.border}`, borderTop:'none', borderRadius:'0 0 8px 8px', padding:16, background:'white' }}>

        {/* ── PRODUCTION STATUS PANEL — rendered via helper component ── */}
        <ProductionStatusPanel po={po} styleData={styleData} checkInData={checkInData} h={h} />

        {/* Check-in info */}
        {checkInData && checkInData.loginTime && (
          <div style={{ marginTop:6, color:T.success, fontWeight:700, fontSize:11,
            marginBottom:8 }}>
            ✓ Checked in at {new Date(checkInData.loginTime).toLocaleTimeString()} ·
            📍 {checkInData.geo?.lat},{checkInData.geo?.lng}
          </div>
        )}
        {gateBlocked && (
          <div style={{ padding:12, borderRadius:8, background:'#e2445c12', border:`1px solid ${T.danger}`, marginBottom:12, fontSize:12.5 }}>
            ⛔ <b>Process flow blocked.</b> {gateInfo}
            {onRaiseException && <div style={{ marginTop:6 }}>
              <Btn small color={T.warning} label="Raise Exception for Manager Approval" onClick={onRaiseException} /></div>}
          </div>
        )}
        <Grid cols={5}>
          <Field label="Technician" value={h.technician} onChange={e=>setH_('technician',e.target.value)} />
          <Field label="Date" type="date" value={h.date} onChange={e=>setH_('date',e.target.value)} />
          <Field label="User Type" value={h.userType} onChange={e=>setH_('userType',e.target.value)}
            options={['Cutting Inline','In-line','Sewing Inline','Finishing Inline','Pre-Final','Final']} />
          <Field label="TOD" value={h.tod} onChange={e=>setH_('tod',e.target.value)} />
          <Field label="Section" value={h.section} onChange={e=>setH_('section',e.target.value)} />
          <Field label="Unit" value={h.unit} onChange={e=>setH_('unit',e.target.value)} />
          <Field label="Line" value={h.line} onChange={e=>setH_('line',e.target.value)} />
          <Field label="Merchandiser" value={h.merchandiser} onChange={e=>setH_('merchandiser',e.target.value)} />
          <Field label="Tech" value={h.tech} onChange={e=>setH_('tech',e.target.value)} />
          <Field label="Colour" value={h.colour} onChange={e=>setH_('colour',e.target.value)}
            options={po ? [...new Set((po.matrix||[]).map(m=>m.color).filter(Boolean))] : null} />
          <Field label="Country" value={h.country} onChange={e=>setH_('country',e.target.value)}
            options={po ? [...new Set((po.matrix||[]).map(m=>m.country).filter(Boolean))] : null} />
          <Field label="Order Qty" type="number" value={h.ordQty} onChange={e=>setH_('ordQty',e.target.value)} />
          <Field label="Cut Qty" type="number" value={h.cutQty} onChange={e=>setH_('cutQty',e.target.value)} />
          <Field label="Sewing Qty" type="number" value={h.sewingQty} onChange={e=>setH_('sewingQty',e.target.value)} />
          <Field label="Finishing Qty" type="number" value={h.finishingQty} onChange={e=>setH_('finishingQty',e.target.value)} />
          <Field label="Pack Qty" type="number" value={h.packQty} onChange={e=>setH_('packQty',e.target.value)} />
          <Field label="Carton Qty" type="number" value={h.ctnQty} onChange={e=>setH_('ctnQty',e.target.value)} />
          <Field label="Ctn Pick Qty" type="number" value={h.ctnPickQty} onChange={e=>setH_('ctnPickQty',e.target.value)} />
          <Field label="Visual Check Qty" type="number" value={h.visualCheckQty} onChange={e=>setH_('visualCheckQty',e.target.value)} />
          <Field label="Measurement Qty" type="number" value={h.measurementQty} onChange={e=>setH_('measurementQty',e.target.value)} />
        </Grid>

        <SubHead n="" title="Styling Check" />
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8 }}>
          {INSP_STYLING_CHECKS.map(s=>(
            <div key={s} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', fontSize:11.5 }}>
              <span>{s}</span>
              <TriState value={styling[s]||''} onChange={v=>setStyling(p=>({...p,[s]:v}))} />
            </div>
          ))}
        </div>

        <SubHead n="" title="AQL Sampling Plan (auto-calculated from order qty)" />
        <Grid cols={4}>
          <Field label="Visual AQL" value={h.aqlVisual} onChange={e=>setH_('aqlVisual',e.target.value)} options={AQL_LEVELS} />
          <Field label="Measurement AQL" value={h.aqlMeasurement} onChange={e=>setH_('aqlMeasurement',e.target.value)} options={AQL_LEVELS} />
          <Field label="Sample Pick" value={h.samplePick} onChange={e=>setH_('samplePick',e.target.value)} />
          <div style={{ fontSize:11.5, background:'#f8fafc', borderRadius:6, padding:8, border:`1px solid ${T.border}` }}>
            <div>Lot <b>{lot.toLocaleString()}</b></div>
            <div>Visual: n=<b>{planV.sampleSize}</b> · Ac <b style={{color:T.success}}>{planV.accept}</b> / Re <b style={{color:T.danger}}>{planV.reject}</b></div>
            <div>Meas.: n=<b>{planM.sampleSize}</b> · Ac <b style={{color:T.success}}>{planM.accept}</b> / Re <b style={{color:T.danger}}>{planM.reject}</b></div>
          </div>
        </Grid>
        <Grid cols={4}>
          <Field label="Fibre (moisture standard)" value={h.fibre} onChange={e=>setH_('fibre',e.target.value)} options={Object.keys(MOISTURE_LIMITS)} />
          <Field label={`Acceptable Range: ${MOISTURE_LIMITS[h.fibre]||'—'}`} value={MOISTURE_LIMITS[h.fibre]||''} onChange={()=>{}} disabled />
          <Field label="Found Moisture %" value={h.foundMoisture} onChange={e=>setH_('foundMoisture',e.target.value)} />
          <Field label="Notes on Over-Moisture" value={h.moistureNotes} onChange={e=>setH_('moistureNotes',e.target.value)} />
        </Grid>

        <SubHead n="" title="Checkpoints — Cutting · Sewing · Finishing · Packing · Safety & Critical" />
        <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:10 }}>
          {Object.entries(INSP_CHECK_SECTIONS).map(([sec,items])=>(
            <div key={sec} style={{ border:`1px solid ${T.border}`, borderRadius:6, overflow:'hidden' }}>
              <div style={{ background:'#f1f5f9', padding:'6px 8px', fontWeight:800, fontSize:10.5, color:T.textMain }}>{sec}</div>
              <div style={{ padding:'4px 8px' }}>
                {items.map(it=>{
                  const k = `${sec}|${it}`; const v = checks[k];
                  return (
                    <div key={it} style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                      gap:4, fontSize:10.5, padding:'2px 0', borderBottom:'1px solid #f8fafc' }}>
                      <span style={{ flex:1 }}>{it}</span>
                      <TriState value={v||''} onChange={val=>setChecks(p=>({...p,[k]:val}))} />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <SubHead n="" title="Workmanship Findings & Defects" />
        <DynamicTable dense
          columns={[
            { label:'Defect Code', key:'code', type:'select', options:defectCodes.map(c=>`${c.code} — ${c.description}`) },
            { label:'Workmanship Finding / Defect', key:'description', type:'textarea', width:'260px' },
            { label:'Critical', key:'critical', type:'number', width:'70px' },
            { label:'Major',    key:'major',    type:'number', width:'70px' },
            { label:'Minor',    key:'minor',    type:'number', width:'70px' },
          ]}
          data={defects} setData={setDefects} onAdd={()=>setDefects([...defects,{ critical:0, major:0, minor:0 }])} />
        <div style={{ display:'flex', gap:20, alignItems:'center', flexWrap:'wrap', fontSize:12.5, fontWeight:800,
          background: verdict==='PASS'?'#00c87512':'#e2445c12', border:`1px solid ${verdict==='PASS'?T.success:T.danger}`,
          borderRadius:8, padding:'10px 14px' }}>
          <span>Total Defects — <span style={{ color:T.danger }}>Critical {totals.critical}</span> ·
            <span style={{ color:T.warning }}> Major {totals.major}</span> · Minor {totals.minor}</span>
          <span>Acceptable at AQL {h.aqlVisual}: <b>{planV.accept}</b></span>
          <span style={{ color: verdict==='PASS'?T.success:T.danger, fontSize:15 }}>RESULT: {verdict}</span>
        </div>

        {/* ═══ MEASUREMENT SPEC SHEET ═══ */}
        {/* ── MEASUREMENT SPEC SHEET ── */}
        {(() => {
          const meas = styleData?.measurements || specData?.measurements;
          const hasSpec = meas?.rows?.length > 0;
          const poSizes = [...new Set((po?.matrix||[]).map(m=>m.size).filter(Boolean))];

          return (
            <>
              {/* Spec connection status banner */}
              <div style={{ marginBottom:10,
                background: hasSpec ? 'linear-gradient(90deg,#e8faf2,#f0fdf4)' : '#fff8e6',
                border:`1.5px solid ${hasSpec ? '#06d6a0' : '#fde68a'}`,
                borderRadius:10, padding:'10px 14px',
                display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
                <span style={{ fontSize:22 }}>{hasSpec ? '📐' : '⚠️'}</span>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:800, fontSize:13,
                    color: hasSpec ? '#0a7d43' : '#7c5c00' }}>
                    {hasSpec
                      ? `✓ Spec Sheet Connected — ${meas.rows.length} POMs · Style ${po?.styleNo}`
                      : `No Spec Sheet — Style ${po?.styleNo||'unknown'}`}
                  </div>
                  <div style={{ fontSize:11.5, color: hasSpec ? '#0a7d43' : '#a07830', marginTop:2 }}>
                    {hasSpec
                      ? `Buyer specs pre-loaded from ${meas.brand||po?.brand||'style registry'} · Sizes: ${(meas.sizes||[]).join(', ')} · QA enters actuals only — system determines Pass/Fail`
                      : 'Upload a spec sheet in Style module to auto-populate measurements. You can still enter measurements manually below.'}
                  </div>
                </div>
                {hasSpec && (
                  <div style={{ display:'flex', gap:8, fontSize:11 }}>
                    <span style={{ background:'white', border:'1px solid #86efac',
                      borderRadius:6, padding:'3px 10px', fontWeight:700, color:'#0a7d43' }}>
                      Tol − / +: per POM
                    </span>
                    <span style={{ background:'white', border:'1px solid #86efac',
                      borderRadius:6, padding:'3px 10px', fontWeight:700, color:'#4f6ef7' }}>
                      {poSizes.length ? `PO Sizes: ${poSizes.join(', ')}` : `Spec Sizes: ${(meas.sizes||[]).join(', ')}`}
                    </span>
                  </div>
                )}
              </div>

              <SubHead n="" title="Measurement Spec Sheet — Buyer Spec vs QA Actual (per size)" />
              <div style={{ background:'#f8fafc', border:`1px solid ${T.border}`, borderRadius:8, padding:12, marginBottom:12 }}>

                {/* Size columns — PO sizes shown first, can add more */}
                <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:10, flexWrap:'wrap' }}>
                  <span style={{ fontSize:11, fontWeight:700, color:T.textSub }}>SIZES BEING CHECKED:</span>
                  {measSizes.map((s,i) => (
                    <span key={i} style={{ display:'inline-flex', alignItems:'center', gap:3 }}>
                      <input value={s} onChange={e=>setMeasSizes(a=>a.map((x,xi)=>xi===i?e.target.value:x))}
                        style={{ width:48, padding:'4px 6px', border:`1px solid ${T.border}`,
                          borderRadius:4, fontSize:11, textAlign:'center', fontWeight:700 }} />
                      <button onClick={()=>setMeasSizes(a=>a.filter((_,xi)=>xi!==i))}
                        style={{ background:'none', border:'none', color:T.danger, cursor:'pointer', fontSize:14, lineHeight:1 }}>×</button>
                    </span>
                  ))}
                  <button onClick={()=>setMeasSizes(a=>[...a,''])}
                    style={{ padding:'4px 10px', background:T.primary, color:'white',
                      border:'none', borderRadius:4, fontSize:11, fontWeight:700, cursor:'pointer' }}>
                    + Add Size
                  </button>
                  <button onClick={()=>setMeasRows(r=>[...r,{ point:'', tolMinus:1, tolPlus:1, tolerance:1, specPerSize:{}, actuals:{}, result:'' }])}
                    style={{ padding:'4px 10px', background:T.teal, color:'white',
                      border:'none', borderRadius:4, fontSize:11, fontWeight:700, cursor:'pointer', marginLeft:'auto' }}>
                    + Add Point
                  </button>
                </div>

                <div style={{ overflowX:'auto' }}>
                <table style={{ borderCollapse:'collapse', fontSize:11.5, width:'100%', minWidth: 500 + measSizes.length * 100 }}>
              <thead>
                <tr style={{ background:'linear-gradient(90deg,#1f1f1f,#2d3748)', color:'white' }}>
                  <th style={{ padding:'8px 10px', textAlign:'left', width:190, fontWeight:700 }}>
                    Measurement Point
                  </th>
                  <th style={{ padding:'8px 6px', textAlign:'center', width:55, fontWeight:700 }}>Tol −</th>
                  <th style={{ padding:'8px 6px', textAlign:'center', width:55, fontWeight:700 }}>Tol +</th>
                  {measSizes.map(s => (
                    <th key={s} style={{ padding:'0', textAlign:'center', minWidth:110 }}>
                      {/* Two sub-columns per size: spec (locked) | actual (QA enters) */}
                      <div style={{ padding:'6px 4px 0', fontWeight:800, fontSize:12 }}>{s||'—'}</div>
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr',
                        borderTop:'1px solid rgba(255,255,255,0.15)', marginTop:4 }}>
                        <div style={{ padding:'4px 6px', fontSize:9, fontWeight:600,
                          opacity:0.8, borderRight:'1px solid rgba(255,255,255,0.15)' }}>
                          SPEC<br/>(from DB)
                        </div>
                        <div style={{ padding:'4px 6px', fontSize:9, fontWeight:600, opacity:0.8 }}>
                          ACTUAL<br/>(QA enters)
                        </div>
                      </div>
                    </th>
                  ))}
                  <th style={{ padding:'8px 8px', textAlign:'center', width:70, fontWeight:700 }}>Result</th>
                  <th style={{ padding:'8px 6px', width:28 }}></th>
                </tr>
              </thead>
              <tbody>
                {measRows.map((row, ri) => {
                  // Use per-size spec values if available, else fall back to single spec field
                  const getSpec = (s) => row.specPerSize?.[s] ? parseFloat(row.specPerSize[s]) : parseFloat(row.spec);
                  const tolMinus = parseFloat(row.tolMinus || row.tolerance || 1);
                  const tolPlus  = parseFloat(row.tolPlus  || row.tolerance || 1);
                  const failSizes = measSizes.filter(s => {
                    const a = parseFloat(row.actuals[s]);
                    const spec = getSpec(s);
                    return !isNaN(a) && !isNaN(spec) &&
                      (a < spec - tolMinus || a > spec + tolPlus);
                  });
                  const displayResult = calcResult(row);
                  const rowBg = displayResult==='Fail' ? '#fff0f0'
                    : displayResult==='Pass' ? '#f0fdf4' : 'white';
                  return (
                    <tr key={ri} style={{ borderBottom:`1px solid ${T.border}`, background:rowBg }}>
                      <td style={{ padding:'5px 8px' }}>
                        <div style={{ fontWeight:700, fontSize:11.5 }}>{row.point||'—'}</div>
                        {(row.tolMinus||row.tolPlus) && (
                          <div style={{ fontSize:9, color:T.textSub }}>
                            tol −{row.tolMinus||'?'} / +{row.tolPlus||'?'}
                          </div>
                        )}
                      </td>
                      {/* Tol − */}
                      <td style={{ padding:'4px 3px', textAlign:'center', fontSize:10.5, color:T.textSub }}>
                        {row.tolMinus || `−${row.tolerance||1}`}
                      </td>
                      {/* Tol + */}
                      <td style={{ padding:'4px 3px', textAlign:'center', fontSize:10.5, color:T.textSub }}>
                        {row.tolPlus  || `+${row.tolerance||1}`}
                      </td>
                      {measSizes.map(s => {
                        const specVal  = row.specPerSize?.[s];  // from spec sheet
                        const specN    = specVal ? parseFloat(specVal) : parseFloat(row.spec);
                        const actual   = parseFloat(row.actuals[s]);
                        const diff     = !isNaN(actual) && !isNaN(specN) ? actual - specN : null;
                        const outOfTol = diff !== null &&
                          (actual < specN - tolMinus || actual > specN + tolPlus);
                        return (
                          <td key={s} style={{ padding:'0', verticalAlign:'middle',
                            borderRight:`1px solid ${T.border}` }}>
                            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr',
                              minWidth:110 }}>
                              {/* SPEC — locked, from spec sheet */}
                              <div style={{ padding:'6px 6px', textAlign:'center',
                                background: specVal ? `${T.primary}08` : '#f8fafc',
                                borderRight:`1px solid ${T.border}`,
                                display:'flex', alignItems:'center', justifyContent:'center' }}>
                                {specVal
                                  ? <span style={{ fontSize:12, fontWeight:700,
                                      color:T.primary }}>{specVal}</span>
                                  : <span style={{ fontSize:10, color:'#c0c4cc' }}>—</span>}
                              </div>
                              {/* ACTUAL — QA enters */}
                              <div style={{ padding:'4px 4px', textAlign:'center',
                                background: outOfTol?'#fdeaec':diff!==null&&!outOfTol?'#f0fdf4':'white' }}>
                                <input type="number" step="0.1"
                                  value={row.actuals[s] !== undefined ? row.actuals[s] : ''}
                                  placeholder="—"
                                  onChange={e=>setMeasRows(a=>a.map((x,xi)=>xi===ri
                                    ? {...x, actuals:{...x.actuals,[s]:e.target.value}}:x))}
                                  style={{ width:'100%', border:`1.5px solid ${outOfTol?T.danger:diff!==null?T.success:T.border}`,
                                    borderRadius:4, padding:'4px 2px', fontSize:12, textAlign:'center',
                                    background:'transparent',
                                    color: outOfTol?T.danger:diff!==null&&!outOfTol?T.success:'inherit',
                                    fontWeight:700, outline:'none' }} />
                                {diff !== null && (
                                  <div style={{ fontSize:9, fontWeight:800, lineHeight:1,
                                    color:outOfTol?T.danger:T.success, marginTop:1 }}>
                                    {diff > 0?'+':''}{diff.toFixed(1)}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        );
                      })}
                      <td style={{ padding:'4px 6px', textAlign:'center' }}>
                        {/* System-determined result — QA cannot override */}
                        <span style={{ display:'inline-block', padding:'3px 9px',
                          borderRadius:8, fontSize:10.5, fontWeight:800,
                          background:displayResult==='Fail'?'#fdeaec':
                            displayResult==='Pass'?'#e9f9f0':'#f4f6fa',
                          color:displayResult==='Fail'?T.danger:
                            displayResult==='Pass'?T.success:T.textSub }}>
                          {displayResult||'—'}
                        </span>
                        {displayResult==='Fail' && (
                          <div style={{ fontSize:8.5, color:T.danger, marginTop:2 }}>Out of tolerance</div>
                        )}
                      </td>
                      <td style={{ padding:'4px 3px', textAlign:'center' }}>
                        <button onClick={()=>setMeasRows(a=>a.filter((_,xi)=>xi!==ri))}
                          style={{ background:'none', border:'none', color:T.danger, cursor:'pointer', fontSize:14 }}>×</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background:'#f1f5f9', fontWeight:800, fontSize:12 }}>
                  <td colSpan={3} style={{ padding:'8px 10px', textAlign:'right' }}>
                    Measurement Summary:
                  </td>
                  <td colSpan={measSizes.length} style={{ padding:'8px 10px', color:measFails>0?T.danger:T.success }}>
                    {measPass} Pass &nbsp;·&nbsp; <span style={{ color:T.danger }}>{measFails} Fail</span>
                    &nbsp;·&nbsp; {measRows.length - measPass - measFails} Not checked
                  </td>
                  <td colSpan={2} style={{ padding:'8px 10px',
                    color: measFails>0 ? T.danger : measPass>0 ? T.success : T.textSub, fontSize:13 }}>
                    {measFails > 0 ? '⚠ FAIL' : measPass > 0 ? '✓ PASS' : '—'}
                  </td>
                </tr>
              </tfoot>
            </table>
            </div>
          <Field label="Measurement Notes / Deviation Comments" type="textarea"
            value={measNotes} onChange={e=>setMeasNotes(e.target.value)} style={{ marginTop:10 }} />

          {/* Measurement fail escalation */}
          {measFails > 0 && (
            <div style={{ marginTop:10, background:'#fdeaec', border:`1.5px solid ${T.danger}`,
              borderRadius:8, padding:'10px 14px', fontSize:12 }}>
              <div style={{ fontWeight:800, color:T.danger, marginBottom:6 }}>
                ⚠ {measFails} measurement point(s) FAILED tolerance — system has flagged this inspection as FAILED on measurement.
              </div>
              <div style={{ fontSize:11.5, color:T.textMain, marginBottom:8 }}>
                The inspection cannot be passed without resolving these deviations.
                Options:
              </div>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                <span style={{ fontSize:11.5 }}>
                  1. <b>Save & Escalate</b> — record the failures and send for manager exception approval to allow shipment.
                </span>
              </div>
              {onRaiseException && (
                <button onClick={onRaiseException}
                  style={{ marginTop:8, padding:'8px 18px', background:T.warning,
                    color:'white', border:'none', borderRadius:7, fontSize:12,
                    fontWeight:700, cursor:'pointer' }}>
                  🚨 Raise Exception for Manager Approval
                </button>
              )}
            </div>
          )}
        </div>
      </>
    );
  })()}

        <Field label="Comments" type="textarea" value={comments} onChange={e=>setComments(e.target.value)} />
        <label style={{ fontSize:12, fontWeight:700, color:T.primary, cursor:'pointer' }}>
          📷 Attach Inspection Images ({images.length})
          <input type="file" multiple accept="image/*" onChange={addImages} style={{ display:'none' }} />
        </label>
        {images.length>0 && (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))', gap:10, marginTop:8 }}>
            {images.map((im,i)=>(
              <div key={i} style={{ border:`1px solid ${T.border}`, borderRadius:6, overflow:'hidden' }}>
                <img src={im.data} alt={im.name} style={{ width:'100%', height:90, objectFit:'cover' }} />
                <input value={im.caption} placeholder="caption"
                  onChange={e=>setImages(a=>a.map((x,ix)=>ix===i?{...x,caption:e.target.value}:x))}
                  style={{ width:'100%', border:'none', borderTop:`1px solid ${T.border}`, padding:4, fontSize:10 }} />
              </div>
            ))}
          </div>
        )}
        <div style={{ fontSize:10.5, fontStyle:'italic', color:T.textSub, margin:'12px 0' }}>
          Note: Inspection of the order does not relieve the factory from his responsibility for the quality of shipped goods.
        </div>
        <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
          <Btn label={`Save as ${verdict==='PASS'?'PASSED':'FAILED'} (AQL verdict)`}
            color={verdict==='PASS'?T.success:T.danger} onClick={()=>save(verdict==='PASS'?'Passed':'Failed')} />
          <Btn label="Override → Passed" outline color={T.success} onClick={()=>save('Passed')} />
          <Btn label="Override → Failed" outline color={T.danger} onClick={()=>save('Failed')} />
          {verdict==='FAIL' && onRaiseCD && (
            <Btn label="🔴 Raise Commercial Decision" color="#dc2626"
              onClick={onRaiseCD}/>
          )}
          <Btn label="🖨 Print / PDF with images" outline onClick={print} />
          {onCancel && <Btn label="Cancel" color="#64748b" onClick={onCancel} />}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  J-05 · FMEA
// ═══════════════════════════════════════════════════════════════════
export function FMEAForm({ po, currentUser, existing, onSave, onCancel }) {
  const [meta, setMeta] = useState(existing?.data?.meta || {
    factory: po?.supplier||'', date: today(), pid: po?.styleNo||'', description:'',
  });
  const [rows, setRows] = useState(existing?.data?.rows || []);
  const totalRPN = rows.reduce((a,r)=>a+rpn(r),0);
  const avgRPN = rows.length ? (totalRPN/rows.length).toFixed(1) : 0;
  const highRisk = rows.filter(r=>rpn(r)>=100).length;

  const save = () => onSave({
    id: existing?.id || uid(), format:'FMEA', iso: fmt('FMEA').iso, poNo: po?.poNo, styleNo: po?.styleNo,
    supplier: po?.supplier, brand: po?.brand, date: meta.date, preparedBy: currentUser.name,
    status: highRisk ? 'In Progress' : 'Completed', totalRPN, avgRPN, highRisk, data:{ meta, rows },
  });

  const print = () => printHTML(`FMEA ${po?.poNo||''}`, `
    <h1>TOQUM · Failure Mode and Effects Analysis (FMEA)</h1>
    <div>Risk Management · Format ${fmt('FMEA').iso} v${fmt('FMEA').version}</div>
    <table><tr><td><b>Factory:</b> ${meta.factory}</td><td><b>Buyer:</b> ${po?.brand||''}</td><td><b>Date:</b> ${meta.date}</td></tr>
      <tr><td><b>PID / Style:</b> ${meta.pid}</td><td colspan="2"><b>Description:</b> ${meta.description}</td></tr></table>
    <table><tr><th>SL</th>${FMEA_COLS.map(c=>`<th>${c.label}</th>`).join('')}<th>RPN</th></tr>
      ${rows.map((r,i)=>`<tr><td>${i+1}</td>${FMEA_COLS.map(c=>`<td>${(r[c.key]||'').toString().replace(/\n/g,'<br/>')}</td>`).join('')}
        <td><b>${rpn(r)}</b></td></tr>`).join('')}
      <tr><th colspan="${FMEA_COLS.length+1}">Total RPN</th><th>${totalRPN}</th></tr></table>
    <p style="font-size:9px">RPN = Severity × Occurrence × Detection. RPN ≥ 100 requires mandatory corrective action.</p>
    <table class="sign"><tr><th>Quality Manager</th><th>Production Manager</th><th>GM</th></tr><tr><td></td><td></td><td></td></tr></table>`);

  return (
    <div>
      <FormatHeader code="FMEA" po={po} />
      <div style={{ border:`1px solid ${T.border}`, borderTop:'none', borderRadius:'0 0 8px 8px', padding:16, background:'white' }}>
        <Grid cols={4}>
          <Field label="Factory" value={meta.factory} onChange={e=>setMeta({...meta,factory:e.target.value})} />
          <Field label="Date" type="date" value={meta.date} onChange={e=>setMeta({...meta,date:e.target.value})} />
          <Field label="PID / Style" value={meta.pid} onChange={e=>setMeta({...meta,pid:e.target.value})} />
          <Field label="Description" value={meta.description} onChange={e=>setMeta({...meta,description:e.target.value})} />
        </Grid>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, margin:'8px 0', fontSize:10.5 }}>
          {Object.entries(FMEA_GUIDE).map(([k,v])=>(
            <div key={k} style={{ background:'#f8fafc', padding:8, borderRadius:6, border:`1px solid ${T.border}` }}>
              <b style={{ textTransform:'capitalize' }}>{k}</b> (1–10): {v}
            </div>
          ))}
        </div>
        <DynamicTable columns={FMEA_COLS} data={rows} setData={setRows}
          onAdd={()=>setRows([...rows,{ severity:'', occurrence:'', detection:'' }])} dense />
        {rows.length>0 && (
          <div style={{ overflowX:'auto', marginBottom:10 }}>
            <table style={{ borderCollapse:'collapse', fontSize:11.5 }}>
              <thead><tr style={{ background:'#f1f5f9' }}>
                <th style={{ padding:'6px 10px' }}>#</th><th style={{ padding:'6px 10px' }}>Failure Mode</th>
                <th style={{ padding:'6px 10px' }}>S</th><th style={{ padding:'6px 10px' }}>O</th>
                <th style={{ padding:'6px 10px' }}>D</th><th style={{ padding:'6px 10px' }}>RPN</th>
                <th style={{ padding:'6px 10px' }}>Priority</th></tr></thead>
              <tbody>{rows.map((r,i)=>{ const v = rpn(r); return (
                <tr key={i} style={{ borderBottom:'1px solid #f1f5f9' }}>
                  <td style={{ padding:'5px 10px' }}>{i+1}</td>
                  <td style={{ padding:'5px 10px' }}>{r.failureMode||'—'}</td>
                  <td style={{ padding:'5px 10px' }}>{r.severity||'—'}</td>
                  <td style={{ padding:'5px 10px' }}>{r.occurrence||'—'}</td>
                  <td style={{ padding:'5px 10px' }}>{r.detection||'—'}</td>
                  <td style={{ padding:'5px 10px', fontWeight:900, color:rpnColor(v) }}>{v}</td>
                  <td style={{ padding:'5px 10px', fontWeight:700, color:rpnColor(v) }}>
                    {v>=100?'HIGH — action mandatory':v>=50?'MEDIUM':v>0?'LOW':'—'}</td>
                </tr>); })}
                <tr style={{ background:'#f8fafc', fontWeight:900 }}>
                  <td colSpan={5} style={{ padding:'6px 10px', textAlign:'right' }}>Total / Average RPN</td>
                  <td style={{ padding:'6px 10px' }}>{totalRPN}</td>
                  <td style={{ padding:'6px 10px' }}>avg {avgRPN}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
        {highRisk>0 && <div style={{ color:T.danger, fontWeight:700, fontSize:12, marginBottom:10 }}>
          ⚠ {highRisk} failure mode(s) with RPN ≥ 100 — corrective action required before closure.
        </div>}
        <div style={{ display:'flex', gap:10 }}>
          <Btn label="Save FMEA" color={T.success} onClick={save} />
          <Btn label="🖨 Print / PDF" outline onClick={print} />
          {onCancel && <Btn label="Cancel" color="#64748b" onClick={onCancel} />}
        </div>
      </div>
    </div>
  );
}

// Registry table (replaces the old separate ISO Codes tab)
export function FormatRegistryTable({ records = [] }) {
  return (
    <Section title="ISO Inspection Format Register — Section J">
      <DataTable columns={['SL','ISO Number','Format Subject','Version','Used In Process Step','Records']}
        rows={FORMAT_REGISTRY.map(f=>({ ...f, id:f.code }))}
        renderCell={(r,c)=>({
          'SL':r.sl, 'ISO Number':<code style={{ fontSize:11 }}>{r.iso}</code>, 'Format Subject':<b>{r.subject}</b>,
          'Version':r.version, 'Used In Process Step':r.stage,
          'Records':<Tag label={`${records.filter(x=>x.format===r.code).length} filed`} color={T.teal} /> }[c])} />
      <div style={{ fontSize:11.5, color:T.textSub, marginTop:8 }}>
        These formats are built into Toqum — the QA fills them directly in the QA module and each completed
        record is filed against its PO. There is no separate ISO code master to maintain.
      </div>
    </Section>
  );
}
